'use client';

import { useEffect, useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getPromotionsData } from '@/actions/new-car/promotions';
import type { ResponseBody } from '@/apis/server/misc/v1/auto-complete';
import type {
  CamelCasePromotionList,
} from '@/apis/server/new-car/v1/promotions';
import {
  searchAutoCompleteList,
} from '@/components/search-filter/auto-complete/configs';
import {
  filterModalAccordionCategoriesConfig,
  filterModalCategoriesConfig,
  searchBarSearchOptionConfig,
} from '@/components/search-filter/configs';
import { useDebouncedCallback } from '@/hooks/use-debounce-callback';
import { useFilterParams } from '@/hooks/use-filter-params';

import {
  FilterLabelList,
} from '../../../../../../components/search-filter/filter-label-list';
import type {
  FilterValue,
} from '../../../../../../components/search-filter/filter-label-list/types';
import {
  FilterModal,
} from '../../../../../../components/search-filter/filter-modal';
import {
  defaultCheckboxValue,
  defaultChoiceValue,
  defaultRangeValue,
  defaultSelectValue,
} from '../../../../../../components/search-filter/filter-modal/configs';
import {
  SearchBar,
} from '../../../../../../components/search-filter/search-bar';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SearchFilterSection: Component = ({
  searchValue,
  setSearchValue,
  carMakeValue,
  setCarMakeValue,
  defaultSearchData,
  brandList,
  isFilterModalOpen,
  submitButtonOption,
  searchBarClassName,
  onFilterClick,
  onModalSubmit,
  onSubmit,
  onLabelRemove,
  onLabelRemoveAll,
  onFilterModalClose,
}) => {
  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState<boolean>(false);
  const [
    isIgnoreSearchInputChange,
    setIsIgnoreSearchInputChange,
  ] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useFilterParams();
  const [autoCompleteList] = useState(searchAutoCompleteList);
  const [
    autoCompleteResults,
    setAutoCompleteResults,
  ] = useState<ResponseBody['data'] | []>([]);
  const [
    promotionList,
    setPromotionList,
  ] = useState<CamelCasePromotionList | null>(null);
  const [
    promotionResults,
    setPromotionResults,
  ] = useState<CamelCasePromotionList | null>(null);
  const [
    selectValues,
    setSelectValues,
  ] = useState(defaultSelectValue(defaultSearchData));
  const [
    rangeValues,
    setRangeValues,
  ] = useState(defaultRangeValue(defaultSearchData));
  const [
    choiceValues,
    setChoiceValues,
  ] = useState(defaultChoiceValue(defaultSearchData));
  const [
    checkboxValues,
    setCheckboxValues,
  ] = useState(defaultCheckboxValue(defaultSearchData));

  useEffect(() => {
    const fetchData = async () => {
      const promotionsActionResult = await getPromotionsData(new FormData());

      if (promotionsActionResult.status !== ACTION_STATUS_SUCCESS) {
        setPromotionList(null);
      } else {
        setPromotionList(promotionsActionResult.data.data);
      }
    };

    fetchData();
  }, []);

  const handleSearchAutoComplete = async () => {
    if (isIgnoreSearchInputChange
      || searchValue.length < 1
      || searchValue === defaultSearchData.query.q
    ) {
      setIsAutoCompleteOpen(false);
      setIsIgnoreSearchInputChange(false);
      return;
    }

    setAutoCompleteResults(
      autoCompleteList.filter(
        item => item
          .toLowerCase().search(searchValue.toLowerCase()) !== -1,
      ),
    );
    setPromotionResults(
      promotionList?.filter(
        item => item.title
          .toLowerCase().search(searchValue.toLowerCase()) !== -1,
      ) ?? null,
    );

    setIsAutoCompleteOpen(true);
  };

  const debouncedAutoComplete = useDebouncedCallback(
    () => {
      handleSearchAutoComplete();
    },
    1000,
    {},
  );

  const handleSearchInputChange = async (v: string) => {
    setSearchValue(v);
    debouncedAutoComplete();
  };

  const handleFilterModalSubmit = () => {
    onModalSubmit?.(
      searchValue,
      carMakeValue,
      selectValues,
      rangeValues,
      choiceValues,
      checkboxValues,
    );
  };

  const handleSearchSubmit = () => {
    onSubmit?.(
      searchValue,
      carMakeValue,
      selectValues,
      rangeValues,
      choiceValues,
      checkboxValues,
    );
    setIsAutoCompleteOpen(false);
  };

  const handleLabelRemove = (i: number, value: FilterValue) => {
    onLabelRemove?.(i, value);
    setFilterValues(filterValues.filter((v, idx) => i !== idx));
  };

  const handleLabelRemoveAll = () => {
    onLabelRemoveAll?.(filterValues);
    setFilterValues([]);
  };

  const handleCarMakeChange = (value: string) => {
    setCarMakeValue(value);
  };

  useEffect(() => {
    setIsAutoCompleteOpen(false);
  }, [defaultSearchData]);

  useEffect(() => {
    setChoiceValues(defaultChoiceValue(defaultSearchData));
    setRangeValues(defaultRangeValue(defaultSearchData));
    setSelectValues(defaultSelectValue(defaultSearchData));
    setCheckboxValues(defaultCheckboxValue(defaultSearchData));
  }, [defaultSearchData]);

  return (
    <>
      <FilterModal
        accordionCategories={filterModalAccordionCategoriesConfig}
        autoCompleteOptions={autoCompleteResults}
        availableYear=""
        brandList={brandList}
        carMakeValue={carMakeValue}
        categories={filterModalCategoriesConfig}
        checkboxValues={checkboxValues}
        choiceValues={choiceValues}
        defaultFilterValues={filterValues}
        defaultSearchData={defaultSearchData}
        isAutoCompleteOpen={isAutoCompleteOpen}
        isOpen={isFilterModalOpen}
        rangeValues={rangeValues}
        searchValue={searchValue}
        selectValues={selectValues}
        setCheckboxValues={setCheckboxValues}
        setChoiceValues={setChoiceValues}
        setRangeValues={setRangeValues}
        setSearchValue={setSearchValue}
        setSelectValues={setSelectValues}
        onAutoCompleteBoxOpen={setIsAutoCompleteOpen}
        onCarMakeChange={handleCarMakeChange}
        onClose={onFilterModalClose}
        onIgnoreSearchInputChange={setIsIgnoreSearchInputChange}
        onSearchInputChange={handleSearchInputChange}
        onSubmit={handleFilterModalSubmit} />
      <SearchBar
        autoCompleteOptions={autoCompleteResults}
        brandList={brandList}
        carMakeValue={carMakeValue}
        checkboxValues={checkboxValues}
        className={searchBarClassName}
        isAutoCompleteOpen={isAutoCompleteOpen}
        promotionOptions={promotionResults}
        rangeValues={rangeValues}
        searchInputOption={searchBarSearchOptionConfig}
        searchValue={searchValue}
        selectValues={selectValues}
        setCheckboxValues={setCheckboxValues}
        setRangeValues={setRangeValues}
        setSearchValue={setSearchValue}
        setSelectValues={setSelectValues}
        submitButtonOption={submitButtonOption}
        showPromotions
        onAutoCompleteBoxOpen={setIsAutoCompleteOpen}
        onCarMakeChange={handleCarMakeChange}
        onFilterClick={onFilterClick}
        onIgnoreSearchInputChange={setIsIgnoreSearchInputChange}
        onSearchInputChange={handleSearchInputChange}
        onSubmit={handleSearchSubmit} />
      <div className={`${filterValues.length > 0 ? styles.containerFilterLabeList : styles.displayNone}`}>
        <FilterLabelList
          hasRemoveAll={filterValues.length > 0}
          values={filterValues}
          onRemove={handleLabelRemove}
          onRemoveAll={handleLabelRemoveAll} />
      </div>
    </>
  );
};
